import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Topbar from '../components/TopBar';
import { useLegacyAppSession } from '../contexts/LegacyAppSessionContext';
import useAppUpdateCheck from '../useAppUpdateCheck';
import { combineResourceActions, getResourceActions } from '../utils/permissionCatalog';

export default function RequireAuthenticatedLayout({ standalone = false }: { standalone?: boolean } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    changeOrganization,
    currentUser,
    handleLogout,
    hasStoredToken,
    isAuthenticated,
    isSessionReady,
    org,
  } = useLegacyAppSession();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const {
    isUpdateAvailable,
    dismiss: dismissUpdateNotice,
    reload: reloadForUpdate,
  } = useAppUpdateCheck();

  const sections = useMemo(
    () => ([
      { key: 'products', label: 'Products', requiredActions: getResourceActions('Products') },
      { key: 'packages', label: 'Packages', requiredActions: getResourceActions('Packages') },
      { key: 'templates', label: 'Label Templates', requiredActions: getResourceActions('Templates') },
      { key: 'prints', label: 'Print Labels', requiredActions: getResourceActions('PrintRuns') },
      { key: 'admin', label: 'Admin', requiredActions: combineResourceActions('Brands', 'OrganizationManagement', 'Imports', 'Facilities', 'UserManagement') },
    ]),
    []
  );

  const toByKey = useMemo(
    () => ({
      products: '/products',
      packages: '/packages',
      prints: '/prints',
      templates: '/templates',
      admin: '/admin',
      profile: '/profile',
    }),
    []
  );

  useEffect(() => {
    if (!showOrgMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!orgDropdownRef.current) return;
      if (!orgDropdownRef.current.contains(event.target as Node)) {
        setShowOrgMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOrgMenu]);

  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  if (!isSessionReady && hasStoredToken) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          returnTo: `${location.pathname || ''}${location.search || ''}${location.hash || ''}`,
        }}
      />
    );
  }

  if (standalone) return <Outlet />;

  const handleTopBarOrganizationChange = async (nextOrganization: any) => {
    await changeOrganization(nextOrganization);
    navigate('/products', { replace: true });
  };

  return (
    <div className="app-shell collapsed" data-testid="app-shell">
      <Topbar
        onStartPrint={() => navigate('/prints/new')}
        org={org}
        showOrgMenu={showOrgMenu}
        setShowOrgMenu={setShowOrgMenu}
        orgDropdownRef={orgDropdownRef}
        changeOrganization={handleTopBarOrganizationChange}
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        userMenuRef={userMenuRef}
        onEditProfile={() => navigate('/profile')}
        onLogout={handleLogout}
      />

      {isUpdateAvailable ? (
        <div className="app-update-notice" role="status" aria-live="polite">
          <div className="app-update-notice__eyebrow">Update available</div>
          <div className="app-update-notice__title">A newer version of Retail ID is ready.</div>
          <div className="app-update-notice__body">
            Reload when you are ready to switch to the latest build.
          </div>
          <div className="app-update-notice__actions">
            <button
              type="button"
              className="button cta"
              onClick={reloadForUpdate}
              data-testid="app-update-reload"
            >
              Reload
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={dismissUpdateNotice}
              data-testid="app-update-dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <div className="shell-body">
        <div className="sidebar-wrap">
          <Sidebar
            sections={sections}
            toByKey={toByKey}
            currentUser={currentUser}
          />
        </div>

        <div className="main-area">
          <div className="content">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
